/* ==========================================================================
   MAISON — theme.js  (vanilla, no dependencies)
   Delegated events + small init functions, re-run on Shopify editor events.
   ========================================================================== */
(() => {
  'use strict';

  const T = window.theme || { routes: {}, settings: {}, strings: {} };
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const debounce = (fn, ms = 250) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const root = (T.routes.root || '/').replace(/\/$/, '');

  /* ---------- Money ---------- */
  function formatMoney(cents, format = T.settings.moneyFormat || '€{{amount_with_comma_separator}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = (n, d = 2, t = ',', dec = '.') => {
      if (isNaN(n) || n == null) return 0;
      n = (n / 100.0).toFixed(d);
      const p = n.split('.');
      return p[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + t) + (p[1] ? dec + p[1] : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: fmt(cents, 2),
      amount_no_decimals: fmt(cents, 0),
      amount_with_comma_separator: fmt(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: fmt(cents, 0, '.', ','),
      amount_with_apostrophe_separator: fmt(cents, 2, "'", '.'),
      amount_with_space_separator: fmt(cents, 2, ' ', ','),
      amount_no_decimals_with_space_separator: fmt(cents, 0, ' ', ',')
    })[k] || fmt(cents, 2));
  }
  window.theme.formatMoney = formatMoney;

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(msg) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
  }

  /* ==========================================================================
     Drawers (cart, menu, filters, search) — focus trap + ESC + scroll lock
     ========================================================================== */
  let lastFocus = null;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function openDrawer(id, opener) {
    const d = document.getElementById(id);
    if (!d) return;
    $$('.drawer.is-open').forEach(o => o !== d && closeDrawer(o, false));
    lastFocus = opener || document.activeElement;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    $$(`[aria-controls="${id}"]`).forEach(b => b.setAttribute('aria-expanded', 'true'));
    const focusTarget = $('[data-autofocus]', d) || $(FOCUSABLE, $('.drawer__panel', d));
    setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 60);
    d.dispatchEvent(new CustomEvent('drawer:open'));
  }
  function closeDrawer(d, restore = true) {
    if (typeof d === 'string') d = document.getElementById(d);
    if (!d || !d.classList.contains('is-open')) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    $$(`[aria-controls="${d.id}"]`).forEach(b => b.setAttribute('aria-expanded', 'false'));
    if (!$('.drawer.is-open')) document.body.classList.remove('overflow-hidden');
    if (restore && lastFocus) lastFocus.focus({ preventScroll: true });
  }
  window.theme.openDrawer = openDrawer;
  window.theme.closeDrawer = closeDrawer;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      if (opener.dataset.drawerOpen === 'CartDrawer' && T.settings.cartType !== 'drawer') return;
      e.preventDefault();
      openDrawer(opener.dataset.drawerOpen, opener);
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) { e.preventDefault(); closeDrawer(closer.closest('.drawer')); }
  });

  document.addEventListener('keydown', (e) => {
    const open = $('.drawer.is-open');
    if (!open) return;
    if (e.key === 'Escape') { closeDrawer(open); return; }
    if (e.key === 'Tab') {
      const f = $$(FOCUSABLE, $('.drawer__panel', open)).filter(el => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------- Native <dialog> modals ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-dialog-open]');
    if (opener) {
      const dlg = document.getElementById(opener.dataset.dialogOpen);
      if (dlg && dlg.showModal) { e.preventDefault(); dlg.showModal(); document.body.classList.add('overflow-hidden'); if (opener.dataset.dialogIndex) dlg.dispatchEvent(new CustomEvent('dialog:index', { detail: +opener.dataset.dialogIndex })); }
      return;
    }
    const closer = e.target.closest('[data-dialog-close]');
    if (closer) { const dlg = closer.closest('dialog'); dlg && dlg.close(); return; }
    if (e.target.tagName === 'DIALOG' && e.target.open) e.target.close(); // backdrop click
  });
  document.addEventListener('close', (e) => {
    if (e.target.tagName === 'DIALOG' && !$('.drawer.is-open')) document.body.classList.remove('overflow-hidden');
  }, true);

  /* ==========================================================================
     Header: sticky, transparent, height vars, mega menu
     ========================================================================== */
  function initHeader() {
    const section = $('.section-header');
    const header = section && $('.header', section);
    if (!header) return;
    const sticky = header.dataset.sticky !== 'false';
    const transparent = header.dataset.transparent === 'true' && document.body.classList.contains('template-index');
    section.classList.toggle('is-sticky', sticky);
    document.body.classList.toggle('has-transparent-header', transparent);

    const setVars = () => {
      const h = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
      document.documentElement.style.setProperty('--sticky-offset', sticky ? h + 'px' : '0px');
    };
    setVars();
    if ('ResizeObserver' in window) new ResizeObserver(setVars).observe(header);

    const onScroll = () => section.classList.toggle('is-scrolled', window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    // Mega menu: hover with intent delay + keyboard
    $$('.header__menu-item[data-has-panel]', header).forEach(item => {
      if (item.dataset.bound) return; item.dataset.bound = '1';
      const link = $('.header__menu-link', item);
      let tIn, tOut;
      const open = () => { clearTimeout(tOut); tIn = setTimeout(() => { $$('.header__menu-item.is-open', header).forEach(i => i !== item && close(i)); item.classList.add('is-open'); section.classList.add('is-hovered'); link.setAttribute('aria-expanded', 'true'); }, 90); };
      const close = (i = item) => { i.classList.remove('is-open'); $('.header__menu-link', i).setAttribute('aria-expanded', 'false'); if (!$('.header__menu-item.is-open', header)) section.classList.remove('is-hovered'); };
      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', () => { clearTimeout(tIn); tOut = setTimeout(() => close(), 140); });
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          if (link.getAttribute('aria-expanded') !== 'true') { e.preventDefault(); clearTimeout(tIn); item.classList.add('is-open'); section.classList.add('is-hovered'); link.setAttribute('aria-expanded', 'true'); const first = $('.mega a, .header__dropdown a', item); first && setTimeout(() => first.focus(), 50); }
        }
      });
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(); });
      item.addEventListener('keydown', (e) => { if (e.key === 'Escape') { close(); link.focus(); } });
    });
  }

  /* ---------- Announcement rotation ---------- */
  function initAnnouncements() {
    $$('[data-announcement]').forEach(bar => {
      if (bar.dataset.bound) return; bar.dataset.bound = '1';
      const items = $$('.announcement__item', bar);
      if (items.length < 2) return;
      let i = 0;
      const speed = (+bar.dataset.speed || 5) * 1000;
      let timer = setInterval(next, speed);
      function next() { items[i].classList.remove('is-active'); i = (i + 1) % items.length; items[i].classList.add('is-active'); }
      bar.addEventListener('mouseenter', () => clearInterval(timer));
      bar.addEventListener('mouseleave', () => { clearInterval(timer); timer = setInterval(next, speed); });
    });
  }

  /* ==========================================================================
     Cart
     ========================================================================== */
  const Cart = {
    sections() { return ['cart-drawer']; },
    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sections(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw data;
      return data;
    },
    render(sections) {
      if (!sections) return;
      const html = sections['cart-drawer'];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-cart-drawer-content]', doc);
      const current = $('[data-cart-drawer-content]');
      if (fresh && current) current.replaceWith(fresh);
      const count = fresh ? fresh.dataset.cartCount : null;
      if (count != null) this.updateCount(+count);
      initCartRecs();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }));
    },
    updateCount(n) {
      // Solo los contadores del icono; el contenido del carrito lateral también lleva data-cart-count
      $$('[data-cart-count]:not([data-cart-drawer-content])').forEach(el => { el.hidden = n === 0; const s = $('span', el); if (s) s.textContent = n; });
    },
    async add(items, btn) {
      btn && btn.classList.add('is-loading');
      try {
        const data = await this.request(T.routes.cartAdd + '.js', { items });
        this.render(data.sections);
        if (T.settings.cartType === 'drawer' && $('#CartDrawer')) openDrawer('CartDrawer', btn);
        else if (T.settings.cartType === 'page') window.location.href = T.routes.cart;
        else toast(T.strings.added);
        return data;
      } catch (err) {
        toast(err && (err.description || err.message) || T.strings.error);
        throw err;
      } finally {
        btn && btn.classList.remove('is-loading');
      }
    },
    async change(line, quantity, el) {
      el && el.classList.add('is-loading');
      try {
        const data = await this.request(T.routes.cartChange + '.js', { line, quantity });
        if ($('[data-cart-page]')) { window.location.reload(); return; }
        this.render(data.sections);
      } catch (err) {
        toast(err && (err.description || err.message) || T.strings.error);
        el && el.classList.remove('is-loading');
      }
    }
  };
  window.theme.cart = Cart;

  // Product forms (PDP + any [data-product-form])
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form || T.settings.cartType === 'page' && !form.dataset.ajax) return;
    e.preventDefault();
    const btn = form.querySelector('[data-add-to-cart]') || form.querySelector('[type="submit"]');
    if (btn && btn.disabled) return;
    const fd = new FormData(form);
    const item = { id: +fd.get('id'), quantity: +(fd.get('quantity') || 1) };
    const props = {};
    for (const [k, v] of fd.entries()) { const m = k.match(/^properties\[(.+)\]$/); if (m && v) props[m[1]] = v; }
    if (Object.keys(props).length) item.properties = props;
    if (fd.get('selling_plan')) item.selling_plan = +fd.get('selling_plan');
    const errorEl = $('[data-form-error]', form);
    if (errorEl) errorEl.hidden = true;
    const buyNow = e.submitter && e.submitter.hasAttribute('data-buy-now');
    if (buyNow) {
      e.submitter.classList.add('is-loading');
      try { await Cart.request(T.routes.cartAdd + '.js', { items: [item] }); window.location.href = root + '/checkout'; }
      catch (err) { e.submitter.classList.remove('is-loading'); if (errorEl) { errorEl.textContent = err.description || err.message || T.strings.error; errorEl.hidden = false; } }
      return;
    }
    try { await Cart.add([item], btn); }
    catch (err) { if (errorEl) { errorEl.textContent = err.description || err.message || T.strings.error; errorEl.hidden = false; } }
  });

  // Quick add buttons (cards, cart recs, lookbook)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    Cart.add([{ id: +btn.dataset.quickAdd, quantity: 1 }], btn).then(() => {
      const card = btn.closest('.card'); card && card.classList.remove('is-quick-open');
    }).catch(() => {});
  });

  // Mobile quick-add toggle
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-quick-toggle]');
    if (!t) return;
    e.preventDefault();
    const card = t.closest('.card');
    $$('.card.is-quick-open').forEach(c => c !== card && c.classList.remove('is-quick-open'));
    card.classList.toggle('is-quick-open');
  });

  // Cart line changes (drawer + page)
  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-cart-remove]');
    if (rm) { e.preventDefault(); Cart.change(+rm.dataset.cartRemove, 0, rm.closest('.cart-item')); return; }
    const q = e.target.closest('[data-cart-qty]');
    if (q) {
      e.preventDefault();
      const input = $('input', q.closest('.quantity'));
      const next = Math.max(0, (+input.value || 0) + (+q.dataset.cartQty));
      input.value = next;
      Cart.change(+input.dataset.line, next, q.closest('.cart-item'));
    }
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('.cart-item .quantity__input');
    if (input) Cart.change(+input.dataset.line, Math.max(0, +input.value || 0), input.closest('.cart-item'));
    const note = e.target.closest('[data-cart-note]');
    if (note) fetch(T.routes.cartUpdate + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
  });

  // Generic quantity steppers (PDP)
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-qty-step]');
    if (!b) return;
    const input = $('input', b.closest('.quantity'));
    const min = +input.min || 1;
    input.value = Math.max(min, (+input.value || min) + (+b.dataset.qtyStep));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // Cart drawer recommendations (lazy)
  function initCartRecs() {
    const box = $('[data-cart-recs]');
    if (!box || box.dataset.loaded) return;
    const pid = box.dataset.productId;
    if (!pid) return;
    box.dataset.loaded = '1';
    fetch(`${T.routes.recommendations}?product_id=${pid}&limit=6&section_id=cart-recommendations`)
      .then(r => r.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const list = $('[data-cart-recs-list]', doc);
        const inCart = (box.dataset.inCart || '').split(',');
        if (!list) return;
        $$('[data-product-id]', list).forEach(el => inCart.includes(el.dataset.productId) && el.remove());
        if (!list.children.length) return;
        box.querySelector('[data-cart-recs-target]').replaceWith(list);
        box.hidden = false;
      }).catch(() => {});
  }

  /* ==========================================================================
     Product card: color swatches swap image + filter quick sizes
     ========================================================================== */
  document.addEventListener('click', (e) => {
    const sw = e.target.closest('[data-card-swatch]');
    if (!sw) return;
    e.preventDefault();
    const card = sw.closest('.card');
    $$('[data-card-swatch]', card).forEach(s => { s.classList.toggle('is-active', s === sw); s.setAttribute('aria-pressed', s === sw); });
    const img = $('.card__img--primary', card);
    if (img && sw.dataset.image) {
      img.srcset = sw.dataset.srcset || '';
      img.src = sw.dataset.image;
    }
    const link = $('.card__title a', card);
    const mediaLink = $('a.card__media', card);
    if (sw.dataset.url) { if (link) link.href = sw.dataset.url; if (mediaLink) mediaLink.href = sw.dataset.url; }
    const color = sw.dataset.cardSwatch;
    $$('[data-quick-color]', card).forEach(b => { b.hidden = b.dataset.quickColor !== color; });
  });

  /* ==========================================================================
     Wishlist (localStorage — per browser)
     ========================================================================== */
  const Wishlist = {
    key: 'maison:wishlist',
    get() { try { return JSON.parse(localStorage.getItem(this.key)) || []; } catch (_) { return []; } },
    set(list) { try { localStorage.setItem(this.key, JSON.stringify(list)); } catch (_) {} this.sync(); },
    has(h) { return this.get().includes(h); },
    toggle(h) { const l = this.get(); const i = l.indexOf(h); i > -1 ? l.splice(i, 1) : l.unshift(h); this.set(l.slice(0, 60)); return i === -1; },
    sync() {
      const list = this.get();
      $$('[data-wishlist-toggle]').forEach(b => b.setAttribute('aria-pressed', list.includes(b.dataset.wishlistToggle)));
      $$('[data-wishlist-count]').forEach(el => { el.hidden = !list.length; const s = $('span', el); if (s) s.textContent = list.length; });
    }
  };
  window.theme.wishlist = Wishlist;
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-wishlist-toggle]');
    if (!b) return;
    e.preventDefault(); e.stopPropagation();
    const added = Wishlist.toggle(b.dataset.wishlistToggle);
    toast(added ? (b.dataset.addedText || '♡') : (b.dataset.removedText || '—'));
    if (!added && b.closest('[data-wishlist-grid]')) { const item = b.closest('[data-grid-item]'); item && item.remove(); renderWishlistEmpty(); }
  });

  // Fetch product cards via ?view=card (templates/product.card.liquid)
  async function fetchCards(handles) {
    const out = await Promise.all(handles.map(h =>
      fetch(`${root}/products/${encodeURIComponent(h)}?view=card`).then(r => r.ok ? r.text() : '').catch(() => '')
    ));
    return out.filter(Boolean);
  }
  function renderWishlistEmpty() {
    const grid = $('[data-wishlist-grid]');
    if (!grid) return;
    const empty = $('[data-wishlist-empty]');
    if (empty) empty.hidden = grid.children.length > 0;
  }
  async function initWishlistPage() {
    const grid = $('[data-wishlist-grid]');
    if (!grid || grid.dataset.bound) return; grid.dataset.bound = '1';
    const cards = await fetchCards(Wishlist.get());
    grid.innerHTML = cards.map(html => `<li data-grid-item>${html}</li>`).join('');
    Wishlist.sync(); renderWishlistEmpty();
  }

  /* ---------- Recently viewed ---------- */
  const RV_KEY = 'maison:recent';
  function trackRecentlyViewed() {
    const el = $('[data-product-handle]');
    if (!el) return;
    const h = el.dataset.productHandle;
    try {
      const list = (JSON.parse(localStorage.getItem(RV_KEY)) || []).filter(x => x !== h);
      list.unshift(h);
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 12)));
    } catch (_) {}
  }
  async function initRecentlyViewed() {
    for (const box of $$('[data-recently-viewed]')) {
      if (box.dataset.bound) continue; box.dataset.bound = '1';
      let list = [];
      try { list = JSON.parse(localStorage.getItem(RV_KEY)) || []; } catch (_) {}
      const current = box.dataset.exclude;
      list = list.filter(h => h !== current).slice(0, +box.dataset.limit || 8);
      if (!list.length) continue;
      const cards = await fetchCards(list);
      if (!cards.length) continue;
      $('[data-recently-viewed-track]', box).innerHTML = cards.map(c => `<div>${c}</div>`).join('');
      box.closest('.shopify-section').hidden = false;
      box.hidden = false;
      Wishlist.sync(); window.dispatchEvent(new Event('resize'));
    }
  }

  /* ==========================================================================
     Sliders (scroll-snap + arrows + progress)
     ========================================================================== */
  function initSliders(scope = document) {
    $$('[data-slider]', scope).forEach(wrap => {
      if (wrap.dataset.bound) return; wrap.dataset.bound = '1';
      const track = $('.slider', wrap);
      if (!track) return;
      const prev = $('[data-slider-prev]', wrap), next = $('[data-slider-next]', wrap);
      const prog = $('.slider-progress span', wrap);
      const step = () => { const item = track.children[0]; return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth; };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
        if (prog) {
          const ratio = track.clientWidth / track.scrollWidth;
          const w = Math.min(100, ratio * 100);
          prog.style.setProperty('--progress-w', w + '%');
          const x = max > 0 ? (track.scrollLeft / max) * ((100 - w) / w) * 100 : 0;
          prog.style.setProperty('--progress-x', x + '%');
          prog.parentElement.hidden = ratio >= .999;
        }
      };
      prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    });
  }

  /* ---------- Benefits dots ---------- */
  function initBenefits() {
    $$('[data-benefits]').forEach(wrap => {
      if (wrap.dataset.bound) return; wrap.dataset.bound = '1';
      const list = $('.benefits__list', wrap), dots = $$('.benefits__dots span', wrap);
      if (!list || !dots.length) return;
      list.addEventListener('scroll', () => {
        const idx = Math.round(list.scrollLeft / (list.children[0].offsetWidth || 1));
        dots.forEach((d, i) => d.classList.toggle('is-active', i === Math.min(idx, dots.length - 1)));
      }, { passive: true });
      if (wrap.dataset.autoplay === 'true' && matchMedia('(max-width: 989px)').matches && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        let i = 0;
        setInterval(() => { i = (i + 1) % list.children.length; list.scrollTo({ left: list.children[i].offsetLeft - list.offsetLeft, behavior: 'smooth' }); }, 3800);
      }
    });
  }

  /* ---------- Reveal on scroll ---------- */
  let revealObserver;
  function initReveal() {
    if (!document.documentElement.classList.contains('has-animations')) return;
    const els = $$('[data-reveal]:not(.is-revealed)');
    if (!('IntersectionObserver' in window)) { els.forEach(el => el.classList.add('is-revealed')); return; }
    revealObserver = revealObserver || new IntersectionObserver((entries) => {
      entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('is-revealed'); revealObserver.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach(el => revealObserver.observe(el));
  }

  /* ==========================================================================
     Predictive search
     ========================================================================== */
  function initSearch() {
    const modal = $('#SearchModal');
    if (!modal || modal.dataset.bound) return; modal.dataset.bound = '1';
    const input = $('input[type="search"]', modal);
    const results = $('[data-predictive-results]', modal);
    const idle = $('[data-search-idle]', modal);
    if (!input || !results || modal.dataset.predictive !== 'true') return;
    let controller;
    const run = debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; if (idle) idle.hidden = false; return; }
      controller && controller.abort();
      controller = new AbortController();
      try {
        const r = await fetch(`${T.routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[limit]=8&resources[type]=product,collection,page,query`, { signal: controller.signal });
        const html = await r.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = $('[data-predictive-content]', doc);
        results.innerHTML = content ? content.innerHTML : '';
        if (idle) idle.hidden = true;
      } catch (_) {}
    }, 220);
    input.addEventListener('input', run);
  }

  /* ==========================================================================
     Collection facets (Section Rendering API)
     ========================================================================== */
  function initFacets() {
    const wrap = $('[data-facets]');
    if (!wrap || wrap.dataset.bound) return; wrap.dataset.bound = '1';
    const sectionId = wrap.dataset.sectionId;

    const buildParams = () => {
      const params = new URLSearchParams();
      $$('form[data-facet-form]').forEach(f => {
        new FormData(f).forEach((v, k) => {
          if (v === '' ) return;
          if (params.getAll(k).includes(v)) return;
          params.append(k, v);
        });
      });
      return params;
    };
    const render = async (search, push = true) => {
      const results = $('[data-collection-results]');
      results && results.classList.add('is-loading');
      try {
        const res = await fetch(`${window.location.pathname}?section_id=${sectionId}&${search}`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        ['[data-collection-results]', '[data-facets-sidebar]', '[data-facets-drawer-body]', '[data-active-facets]', '[data-product-count]'].forEach(sel => {
          const fresh = $$(sel, doc), cur = $$(sel);
          cur.forEach((c, i) => { if (fresh[i]) c.innerHTML = fresh[i].innerHTML; });
        });
        if (push) history.pushState({ search }, '', `${window.location.pathname}${search ? '?' + search : ''}`);
        Wishlist.sync(); initReveal();
      } finally {
        $('[data-collection-results]') && $('[data-collection-results]').classList.remove('is-loading');
      }
    };
    const onChange = debounce((form) => {
      // sync sort between bar & drawer forms
      const params = buildParams();
      if (form) {
        // when a form changed, its values win for keys it owns
        const own = new FormData(form);
        const keys = new Set([...own.keys()]);
        keys.forEach(k => { params.delete(k); own.getAll(k).forEach(v => v !== '' && params.append(k, v)); });
      }
      params.delete('page');
      render(params.toString());
    }, 350);

    document.addEventListener('change', (e) => {
      const form = e.target.closest('form[data-facet-form]');
      if (!form) return;
      // Mirror identical inputs in other forms (sidebar <-> drawer)
      const name = e.target.name, val = e.target.value;
      $$(`form[data-facet-form] [name="${CSS.escape(name)}"]`).forEach(other => {
        if (other === e.target) return;
        if (other.type === 'checkbox' || other.type === 'radio') { if (other.value === val) other.checked = e.target.checked; }
        else other.value = val;
      });
      onChange(form);
    });
    document.addEventListener('submit', (e) => { if (e.target.closest('form[data-facet-form]')) { e.preventDefault(); onChange(e.target); } });
    document.addEventListener('click', (e) => {
      const a = e.target.closest('[data-facet-remove]');
      if (!a) return;
      e.preventDefault();
      const url = new URL(a.href, window.location.origin);
      render(url.searchParams.toString());
      // uncheck inputs locally to keep forms in sync
      setTimeout(() => { $$('form[data-facet-form]').forEach(f => f.reset()); }, 0);
    });
    window.addEventListener('popstate', (e) => render((e.state && e.state.search) || window.location.search.slice(1), false));

    // Grid density toggle (desktop)
    document.addEventListener('click', (e) => {
      const b = e.target.closest('[data-grid-cols]');
      if (!b) return;
      $$('[data-grid-cols]').forEach(x => x.setAttribute('aria-pressed', x === b));
      const grid = $('[data-collection-results] .grid');
      if (grid) grid.dataset.cols = b.dataset.gridCols;
      try { localStorage.setItem('maison:grid', b.dataset.gridCols); } catch (_) {}
    });
    try { const saved = localStorage.getItem('maison:grid'); if (saved) { const b = $(`[data-grid-cols="${saved}"]`); b && b.click(); } } catch (_) {}
  }

  /* ==========================================================================
     Product page
     ========================================================================== */
  function initProduct(scope = document) {
    $$('[data-product]', scope).forEach(product => {
      if (product.dataset.bound) return; product.dataset.bound = '1';
      const dataEl = $('script[data-product-json]', product);
      if (!dataEl) return;
      const data = JSON.parse(dataEl.textContent);
      const form = $('form[data-product-form]', product);
      const idInput = form && $('input[name="id"]', form);
      const picker = $('[data-variant-picker]', product);
      const atc = $('[data-add-to-cart]', product);
      const priceEl = $('[data-product-price]', product);
      const inventoryEl = $('[data-inventory]', product);
      const stickyAtc = $('[data-sticky-atc]');
      const gallery = $('[data-gallery]', product);
      const optionCount = data.options.length;

      const selected = () => picker ? $$('fieldset[data-option-index], select[data-option-index]', picker).map(el => {
        if (el.tagName === 'SELECT') return el.value;
        const c = $('input:checked', el); return c ? c.value : null;
      }) : [];

      const updateAvailability = (sel) => {
        if (!picker) return;
        $$('[data-option-index]', picker).forEach((group) => {
          const idx = +group.dataset.optionIndex;
          $$('input, option', group).forEach(inp => {
            const ok = data.variants.some(v => v.available && v.options[idx] === inp.value &&
              v.options.slice(0, idx).every((o, j) => o === sel[j]));
            inp.classList.toggle('is-unavailable', !ok);
          });
          const label = $('[data-selected-value]', group.closest('.option') || group);
          if (label) label.textContent = sel[idx] || '';
        });
      };

      const setMedia = (variant) => {
        if (!gallery || !variant || !variant.featured_media) return;
        const item = $(`[data-media-id="${variant.featured_media.id}"]`, gallery);
        if (!item) return;
        const track = $('.product-gallery__track', gallery);
        if (matchMedia('(min-width: 990px)').matches && !gallery.classList.contains('product-gallery--thumbs')) {
          if (item !== track.firstElementChild && gallery.dataset.moveVariant === 'true') { track.prepend(item); }
        } else {
          track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: 'smooth' });
        }
      };

      const onVariant = () => {
        const sel = selected();
        const variant = data.variants.find(v => v.options.every((o, i) => o === sel[i]));
        updateAvailability(sel);
        if (!variant) {
          if (atc) { atc.disabled = true; $('span', atc).textContent = T.strings.unavailable; }
          return;
        }
        if (idInput) { idInput.value = variant.id; idInput.dispatchEvent(new Event('change', { bubbles: true })); }
        if (priceEl) {
          const sale = variant.compare_at_price && variant.compare_at_price > variant.price;
          priceEl.innerHTML = sale
            ? `<span class="visually-hidden">${priceEl.dataset.saleLabel || ''}</span><span class="price__sale">${formatMoney(variant.price)}</span><s class="price__compare">${formatMoney(variant.compare_at_price)}</s>`
            : `<span>${formatMoney(variant.price)}</span>`;
        }
        if (atc) {
          atc.disabled = !variant.available;
          $('span', atc).textContent = variant.available ? (atc.dataset.label || T.strings.addToCart) : T.strings.soldOut;
        }
        if (inventoryEl) {
          const inv = (data.inventory || {})[variant.id];
          inventoryEl.classList.remove('inventory--low', 'inventory--out');
          if (!variant.available) { inventoryEl.classList.add('inventory--out'); inventoryEl.textContent = inventoryEl.dataset.out; }
          else if (inv != null && inv > 0 && inv <= (+inventoryEl.dataset.threshold || 5)) { inventoryEl.classList.add('inventory--low'); inventoryEl.textContent = inventoryEl.dataset.low.replace('[count]', inv); }
          else inventoryEl.textContent = inventoryEl.dataset.in;
        }
        if (stickyAtc) {
          const s = $('select', stickyAtc); if (s) s.value = variant.id;
          const sp = $('[data-sticky-price]', stickyAtc); if (sp) sp.textContent = formatMoney(variant.price);
          const sb = $('[data-sticky-atc-btn]', stickyAtc); if (sb) { sb.disabled = !variant.available; sb.textContent = variant.available ? (sb.dataset.label) : T.strings.soldOut; }
        }
        setMedia(variant);
        if (product.dataset.updateUrl !== 'false') {
          const url = new URL(window.location.href); url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url.toString());
        }
      };

      picker && picker.addEventListener('change', onVariant);
      if (picker) updateAvailability(selected());

      // Sticky add to cart
      if (stickyAtc && atc && 'IntersectionObserver' in window) {
        new IntersectionObserver(([en]) => {
          const past = !en.isIntersecting && en.boundingClientRect.top < 0;
          stickyAtc.classList.toggle('is-visible', past);
          stickyAtc.setAttribute('aria-hidden', !past);
        }).observe(atc);
        const s = $('select', stickyAtc);
        s && s.addEventListener('change', () => {
          const v = data.variants.find(x => x.id === +s.value);
          if (!v || !picker) return;
          v.options.forEach((val, i) => {
            const g = $(`[data-option-index="${i}"]`, picker);
            if (!g) return;
            if (g.tagName === 'SELECT') g.value = val;
            else { const inp = $$('input', g).find(x => x.value === val); if (inp) inp.checked = true; }
          });
          onVariant();
        });
        const sb = $('[data-sticky-atc-btn]', stickyAtc);
        sb && sb.addEventListener('click', () => form && form.requestSubmit(atc));
      }

      initGallery(product);
      void optionCount;
    });
  }

  function initGallery(scope) {
    const gallery = $('[data-gallery]', scope);
    if (!gallery) return;
    const track = $('.product-gallery__track', gallery);
    const items = $$('.product-gallery__item', track);
    const dots = $$('.product-gallery__dots span', gallery);
    const counter = $('[data-gallery-counter]', gallery);
    const thumbs = $$('.product-gallery__thumb', gallery);
    const setActive = (i) => {
      dots.forEach((d, j) => d.classList.toggle('is-active', j === i));
      thumbs.forEach((t, j) => t.setAttribute('aria-current', j === i));
      if (counter) counter.textContent = `${i + 1} / ${items.length}`;
      // Pause videos not in view
      items.forEach((it, j) => { if (j !== i) { const v = $('video', it); v && v.pause(); } });
    };
    track.addEventListener('scroll', debounce(() => {
      if (track.scrollWidth <= track.clientWidth) return;
      setActive(Math.round(track.scrollLeft / track.clientWidth));
    }, 60), { passive: true });
    thumbs.forEach((t, i) => t.addEventListener('click', () => {
      setActive(i);
      if (track.scrollWidth > track.clientWidth) track.scrollTo({ left: items[i].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      else items[i].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }));
    // Zoom dialog: scroll to clicked image
    const zoom = document.getElementById(gallery.dataset.zoomId);
    if (zoom) zoom.addEventListener('dialog:index', (e) => {
      const target = $$('img', zoom)[e.detail];
      target && setTimeout(() => target.scrollIntoView({ block: 'start' }), 30);
    });
  }

  /* ---------- Product recommendations / complementary ---------- */
  function initRecommendations() {
    $$('[data-recommendations]').forEach(async (el) => {
      if (el.dataset.bound) return; el.dataset.bound = '1';
      try {
        const r = await fetch(el.dataset.url);
        const html = await r.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('[data-recommendations]', doc);
        if (fresh && fresh.innerHTML.trim().length && $('.card, .mini-card', fresh)) {
          el.innerHTML = fresh.innerHTML;
          el.hidden = false;
          const sec = el.closest('.shopify-section'); if (sec) sec.hidden = false;
          initSliders(el); Wishlist.sync(); initReveal();
        } else {
          const sec = el.closest('.shopify-section'); if (sec && el.dataset.hideEmpty !== 'false') sec.hidden = true;
        }
      } catch (_) {}
    });
  }

  /* ---------- Hero video autoplay respect reduced motion ---------- */
  function initVideos() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) $$('video[autoplay]').forEach(v => { v.removeAttribute('autoplay'); v.pause(); });
  }

  /* ---------- Localization selects auto-submit ---------- */
  document.addEventListener('change', (e) => {
    const s = e.target.closest('[data-localization-select]');
    if (s) s.form.submit();
  });

  /* ---------- Share ---------- */
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-share]');
    if (!b) return;
    e.preventDefault();
    const url = b.dataset.url || window.location.href;
    if (navigator.share) { try { await navigator.share({ title: document.title, url }); } catch (_) {} }
    else if (navigator.clipboard) { await navigator.clipboard.writeText(url); toast(b.dataset.copied || 'OK'); }
  });

  /* ==========================================================================
     Boot + Theme editor support
     ========================================================================== */
  function init(scope = document) {
    initHeader(); initAnnouncements(); initSliders(scope); initBenefits(); initReveal();
    initSearch(); initFacets(); initProduct(scope); initRecommendations(); initVideos();
    initCartRecs(); initWishlistPage(); initRecentlyViewed(); Wishlist.sync();
  }

  document.addEventListener('DOMContentLoaded', () => {
    trackRecentlyViewed();
    if (matchMedia('(max-width: 989px)').matches) $$('.footer__menu').forEach(d => { d.open = false; });
    init();
  });

  if (window.Shopify && Shopify.designMode) {
    document.documentElement.classList.add('shopify-design-mode');
    document.addEventListener('shopify:section:load', (e) => { init(e.target); $$('[data-reveal]', e.target).forEach(el => el.classList.add('is-revealed')); });
    document.addEventListener('shopify:section:select', (e) => {
      if (e.target.querySelector('#CartDrawer')) openDrawer('CartDrawer');
    });
    document.addEventListener('shopify:section:deselect', (e) => {
      if (e.target.querySelector('#CartDrawer')) closeDrawer('CartDrawer');
    });
    document.addEventListener('shopify:block:select', (e) => {
      const item = e.target.closest('.header__menu-item'); if (item) item.classList.add('is-open');
      const slide = e.target.closest('.slider > *'); if (slide) slide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      const ann = e.target.closest('.announcement__item'); if (ann) { $$('.announcement__item', ann.parentElement).forEach(i => i.classList.toggle('is-active', i === ann)); }
    });
    document.addEventListener('shopify:block:deselect', (e) => { const item = e.target.closest('.header__menu-item'); if (item) item.classList.remove('is-open'); });
  }
})();
